import React, { useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { getCouponPermissions, createCouponPermission, deleteCouponPermission, getCouponsByStatus, getAllAdmins } from '../../../../services/couponAdminApi';
import { useToast } from '../../../../hooks/use-toast';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/ui/table';
import { Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '../../../../components/ui/alert';

const CouponPermissionsPage = () => {
    const { toast } = useToast();
    const [permissions, setPermissions] = useState([]);
    const [coupons, setCoupons] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const { register, handleSubmit, control, reset, formState: { errors } } = useForm({
        defaultValues: {
            coupon_id: '',
            admin_id: '',
            group_name: ''
        }
    });

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [permsRes, couponsRes, adminsRes] = await Promise.all([
                getCouponPermissions(),
                getCouponsByStatus('ACTIVE'),
                getAllAdmins()
            ]);
            
            // EDIT: Filter coupons to only include those that can be manually activated.
            const activatableCoupons = couponsRes.data.filter(
                c => c.applicability_type === 'SPECIFIC_SELLERS'
            );

            setPermissions(permsRes.data);
            setCoupons(activatableCoupons);
            setAdmins(adminsRes.data);
            setError('');
        } catch (err) {
            const errorMsg = err.response?.data?.detail || 'Failed to fetch necessary data.';
            setError(errorMsg);
            toast({ variant: 'destructive', title: 'Error', description: errorMsg });
        } finally {
            setIsLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onSubmit = async (data) => {
        const payload = {
            coupon_id: parseInt(data.coupon_id, 10),
            admin_id: data.admin_id ? parseInt(data.admin_id, 10) : null,
            group_name: data.group_name || null,
        };

        try {
            await createCouponPermission(payload);
            toast({ title: 'Success', description: 'Permission created successfully.' });
            reset();
            fetchData();
        } catch (err) {
            const errorMsg = err.response?.data?.detail || 'Failed to create permission.';
            toast({ variant: 'destructive', title: 'Error', description: errorMsg });
        }
    };

    const handleDelete = async (permissionId) => {
        if (window.confirm('Are you sure you want to delete this permission?')) {
            try {
                await deleteCouponPermission(permissionId);
                toast({ title: 'Success', description: 'Permission deleted.' });
                fetchData();
            } catch (err) {
                const errorMsg = err.response?.data?.detail || 'Failed to delete permission.';
                toast({ variant: 'destructive', title: 'Error', description: errorMsg });
            }
        }
    };

    if (isLoading) return <div>Loading permissions...</div>;

    return (
        <div className="space-y-6">
            <h2 className="text-3xl font-bold tracking-tight">Coupon Activation Permissions</h2>
            {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
            
            <Card>
                <CardHeader>
                    <CardTitle>Add New Permission</CardTitle>
                    <CardDescription>Grant an admin or an entire group permission to activate a specific coupon. Only coupons with an applicability of "Specific Sellers" will appear here.</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <Label htmlFor="coupon_id">Coupon</Label>
                                <Controller
                                    name="coupon_id"
                                    control={control}
                                    rules={{ required: 'Coupon is required' }}
                                    render={({ field }) => (
                                        <Select onValueChange={field.onChange} value={field.value} disabled={coupons.length === 0}>
                                            <SelectTrigger><SelectValue placeholder="Select a coupon..." /></SelectTrigger>
                                            <SelectContent>
                                                {coupons.length > 0 ? (
                                                    coupons.map(c => <SelectItem key={c.id} value={String(c.id)}>{c.code} - {c.name}</SelectItem>)
                                                ) : (
                                                    <SelectItem value="no-coupons" disabled>No activatable coupons found</SelectItem>
                                                )}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                                {errors.coupon_id && <p className="text-sm text-destructive mt-1">{errors.coupon_id.message}</p>}
                            </div>
                            <div>
                                <Label htmlFor="admin_id">Specific Admin (Optional)</Label>
                                <Controller
                                    name="admin_id"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            onValueChange={(value) => {
                                                field.onChange(value === 'NONE_VALUE' ? '' : value);
                                            }}
                                            value={field.value || ''}
                                        >
                                            <SelectTrigger><SelectValue placeholder="Select an admin..." /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="NONE_VALUE">None</SelectItem>
                                                {admins.map(a => <SelectItem key={a.id} value={String(a.id)}>{a.email}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </div>
                            <div>
                                <Label htmlFor="group_name">Group Name (Optional)</Label>
                                <Input id="group_name" {...register('group_name')} placeholder="e.g., cx-team" />
                                <p className="text-xs text-muted-foreground mt-1">Use either Specific Admin or Group Name, not both.</p>
                            </div>
                        </div>
                        <Button type="submit">Add Permission</Button>
                    </form>
                </CardContent>
            </Card>

            <Card>
                <CardHeader><CardTitle>Existing Permissions</CardTitle></CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Coupon Code</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Target</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {permissions.length > 0 ? permissions.map(p => (
                                <TableRow key={p.id}>
                                    <TableCell className="font-medium">{p.coupon_code}</TableCell>
                                    <TableCell>{p.admin_id ? 'Admin' : 'Group'}</TableCell>
                                    <TableCell>{p.admin_id ? p.admin_email : p.group_name}</TableCell>
                                    <TableCell>{p.is_active ? 'Active' : 'Inactive'}</TableCell>
                                    <TableCell className="text-right">
                                        <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow><TableCell colSpan="5" className="text-center">No permissions found.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
};

export default CouponPermissionsPage;
